import {AsyncPipe} from '@angular/common';
import {Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {PaginationComponent} from '../../../../shared/components/pagination/pagination.component';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {TranslatePipe} from '@ngx-translate/core';
import {InputWrapperComponent} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {ViewPreferenceService} from '../../../../core/services/view-preference.service';
import {ApiDeviceGroupResponseDto} from '@birco/rainmesh-api';
import {PageEvent} from '@angular/material/paginator';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {withResetPageOnOtherFilterChange} from '../../../../shared/utils/filter-utils';
import {AreaItemCardComponent} from '../area-item-card/area-item-card.component';
import {AreaItemRowComponent} from '../area-item-row/area-item-row.component';
import {DeviceItemRowComponent} from '../device-item-row/device-item-row.component';
import {BehaviorSubject, combineLatest, map} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectDeviceState} from '../../state/device.selectors';

@Component({
    selector: 'app-tab-area-list',
    imports: [
        PaginationComponent,
        IconComponent,
        ReactiveFormsModule,
        AsyncPipe,
        TranslatePipe,
        InputWrapperComponent,
        AreaItemCardComponent,
        AreaItemRowComponent,
        DeviceItemRowComponent
    ],
    templateUrl: './tab-area-list.component.html',
    styleUrl: './tab-area-list.component.scss'
})
export class TabAreaListComponent implements OnInit {
    private static readonly viewPreferenceKey = 'devices.tab-area-list';
    private _fb = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly viewPreferenceService = inject(ViewPreferenceService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly deviceGroupsSubject = new BehaviorSubject<ApiDeviceGroupResponseDto[]>([]);

    @Input() set deviceGroups(value: ApiDeviceGroupResponseDto[] | null) {
      this.deviceGroupsSubject.next(value ?? []);
    }
    @Input() expandedById: Record<string, boolean> | null = {};
    @Input() loading = false;
    @Input() totalCount: number | null = 0;
    @Input() page = 0;
    @Input() pageSize = 12;

    @Output() filterChange = new EventEmitter<{searchTerm?: string; page?: number; pageSize?: number}>();
    @Output() create = new EventEmitter<void>();
    @Output() triggerAction = new EventEmitter<{id: string, value: boolean}>();
    @Output() setExpanded = new EventEmitter<{id: string; isExpanded: boolean}>();
    @Output() edit = new EventEmitter<string>();
    @Output() delete = new EventEmitter<string>();

    isListView = true;
    filterForm: FormGroup;
    readonly deviceGroupsView$ = combineLatest([
      this.deviceGroupsSubject,
      this.store.select(selectDeviceState),
    ]).pipe(
      map(([deviceGroups, deviceState]) =>
        deviceGroups.map((group) => ({
          deviceGroup: group,
          devices: (group.devices ?? [])
            .map((device) => deviceState.entities[device.id] ?? device)
            .sort((a, b) => a.name.localeCompare(b.name)),
        })),
      ),
    );

    constructor() {
        this.filterForm = this._fb.group({
          searchTerm: [undefined],
          page: [0],
          pageSize: [12],
        });
        this.isListView = this.viewPreferenceService.getIsListView(TabAreaListComponent.viewPreferenceKey, true);
    }

    ngOnInit() {
      this.filterForm.patchValue({
        page: this.page,
        pageSize: this.pageSize,
      }, {emitEvent: false});

      this.filterForm.valueChanges.pipe(
        withResetPageOnOtherFilterChange(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((filter) => {
        this.filterChange.emit({
          searchTerm: filter.searchTerm ?? undefined,
          page: filter.page ?? undefined,
          pageSize: filter.pageSize ?? undefined,
        });
      });

      queueMicrotask(() => {
        this.filterChange.emit(this.filterForm.getRawValue());
      });
    }

    onChangePage(event: PageEvent) {
      this.filterForm.controls['page'].patchValue(event.pageIndex);
      this.filterForm.controls['pageSize'].patchValue(event.pageSize);
    }

    toggleView() {
        this.isListView = !this.isListView;
        this.viewPreferenceService.setIsListView(TabAreaListComponent.viewPreferenceKey, this.isListView);
    }

    onGroupExpandedChange(id: string, isExpanded: boolean) {
      this.setExpanded.emit({id, isExpanded});
    }
}
